<template>
  <div
    v-if="user && user.role"
    ref="sidebarMenuRef"
    class="panelGeneral d-flex flex-column"
    id="sidebarMenu"
    :class="['panelGeneral d-flex flex-column', { open: props.isOpen }]"
  >
    <div v-if="isMobile" class="logo-container">
      <ThemedIcon name="LuqueLogoMuni" class="imgLuque" />
      <svg
        class="arrow-close"
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="26"
        viewBox="0 0 25 26"
        fill="none"
        @click="toggleSidebar"
      >
        <g clip-path="url(#clip0_8544_37562)">
          <path
            d="M-5.00679e-06 12.5518C-5.00679e-06 15.024 0.733108 17.4408 2.10663 19.4964C3.48014 21.552 5.43238 23.1542 7.71645 24.1003C10.0005 25.0463 12.5139 25.2939 14.9386 24.8116C17.3634 24.3293 19.5907 23.1387 21.3388 21.3906C23.087 19.6424 24.2775 17.4151 24.7598 14.9904C25.2421 12.5656 24.9946 10.0523 24.0485 7.76821C23.1024 5.48414 21.5002 3.53191 19.4446 2.15839C17.389 0.78487 14.9723 0.0517578 12.5 0.0517578C9.18589 0.0553423 6.00855 1.37345 3.66512 3.71688C1.32169 6.06031 0.00357945 9.23765 -5.00679e-06 12.5518ZM22.9167 12.5518C22.9167 14.612 22.3057 16.6259 21.1611 18.3389C20.0165 20.052 18.3897 21.3871 16.4863 22.1755C14.5829 22.9639 12.4884 23.1702 10.4678 22.7683C8.44717 22.3663 6.5911 21.3742 5.1343 19.9175C3.6775 18.4607 2.68541 16.6046 2.28348 14.5839C1.88155 12.5633 2.08784 10.4689 2.87625 8.56547C3.66466 6.66207 4.99979 5.03521 6.7128 3.89062C8.42582 2.74602 10.4398 2.13509 12.5 2.13509C15.2617 2.13812 17.9095 3.23656 19.8623 5.18941C21.8152 7.14226 22.9136 9.79002 22.9167 12.5518ZM8.94375 11.0788L13.8542 6.17051L15.324 7.64342L10.4167 12.5518L15.2844 17.4195L13.8115 18.8924L8.94375 14.0247C8.55318 13.634 8.33377 13.1042 8.33377 12.5518C8.33377 11.9993 8.55318 11.4695 8.94375 11.0788Z"
            fill="var(--text-primary)"
          />
        </g>
        <defs>
          <clipPath id="clip0_8544_37562">
            <rect
              width="25"
              height="25"
              fill="white"
              transform="matrix(-1 0 0 1 25 0.0517578)"
            />
          </clipPath>
        </defs>
      </svg>
    </div>
    <div v-else>
      <router-link to="/home-agentes">
        <ThemedIcon name="LuqueLogoMuni" class="imgLuque" />
      </router-link>
    </div>

    <div class="navScrollable">
      <div class="NavMuni d-flex flex-column gap-3">
        <div
          v-for="route in filteredMunicipalRoutes"
          :key="route.name"
          class="bn3 d-flex align-items-center"
          active-class="link-activo"
        >
          <router-link
            :to="{ name: route.name }"
            @click="emit('close')"
            class="d-flex align-items-center w-100 gap"
            active-class="link-activo"
          >
            <img
              :src="
                getIconPath(
                  theme === 'dark' ? route.meta.iconDark : route.meta.iconLight,
                )
              "
              class="iconos"
            />
            <h2 class="h2-defaultMuni">{{ route.meta.title }}</h2>

            <div
              v-if="
                route.meta.badgeResolver &&
                counters[route.meta.badgeResolver] > 0
              "
              class="image-container"
            >
              <img src="@/assets/images/LuqueCounter.svg" />
              <span class="turnos-value">
                {{ counters[route.meta.badgeResolver] }}
              </span>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <div v-if="isMobile" class="actions-container">
      <ThemedIcon class="action" @click="toggleTheme" name="theme" />
      <ThemedIcon class="action" @click="openNotifications" name="IconNoti" />
      <ThemedIcon class="action" @click="openCloseSesion" name="IconOff" />
    </div>
  </div>

  <ModalManager ref="modalManagerRef" />
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex"; // Asumiendo Vuex por "$store" en tu código
import { navBarRoutes } from "@/router/router-utils";
import ModalManager from "@/components/Utils/Modales/ModalManager.vue";
import NotificacionesPanel from "./NotificacionesPanel.vue";
import ModalCerrarSesion from "../Modals/ModalCerrarSesion.vue";
import { useSidebarBadges } from "@/services/helpers/useSidaBarBadges";
import ThemedIcon from "@/components/Utils/ThemedIcon.vue";
import { toggleTheme } from "@/services/helpers/themeHelper";

// --- PROPS & EMITS ---
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const theme = ref("light");

onMounted(() => {
  theme.value = document.documentElement.getAttribute("data-theme");

  window.addEventListener("theme-change", () => {
    theme.value = document.documentElement.getAttribute("data-theme");
  });
});

// --- STATE & STORE ---
const store = useStore();
const sidebarMenuRef = ref(null);
const modalManagerRef = ref(null);

const isMobile = computed(() => window.innerWidth <= 770);
const user = computed(() => store.state.user);
const filteredMunicipalRoutes = computed(() => navBarRoutes());

// --- LÓGICA DE CONTADORES (Extrada) ---
const { counters } = useSidebarBadges();

// --- FUNCIONES AUXILIARES ---
const getIconPath = (iconName) => {
  // Nota: En Vite, require dinámico a veces da problemas.
  // Si usas Webpack funciona. Si usas Vite, considera usar new URL(..., import.meta.url).href
  try {
    return require(`@/assets/images/${iconName}`);
  } catch (e) {
    return "";
  }
};

const toggleSidebar = () => {
  store.dispatch("toggleSidebar");
};

// --- GESTIÓN DE MODALES ---
const openFirstModal = (modal, modalProps) => {
  if (modalManagerRef.value) {
    modalManagerRef.value.openModal(modal, modalProps);
  }
};

const openNotifications = () => {
  openFirstModal(NotificacionesPanel, {
    size: "auto",
    scrollable: false,
    position: "bottom",
    draggable: true,
    dragDismiss: {
      enabled: true,
      minThresholdPx: 72,
      minVelocity: 0.45,
    },
  });
};

const openCloseSesion = () => {
  openFirstModal(ModalCerrarSesion, {
    size: "auto",
    scrollable: false,
    showX: false,
    noClose: true,
  });
};
</script>

<style scoped>
a {
  color: var(--text-primary) !important;
}
.panelGeneral {
  position: fixed;
  height: 100dvh;
  background: var(--gray);
  width: 15.227vw;
  padding: 3.339dvh 2.196vw;
  z-index: 1;
  gap: 5.008dvh;
  transition:
    transform 0.3s ease-in-out,
    width 0.3s ease-in-out;
  transform: translateX(-100%);
}

.imgLuque {
  width: 9.59vw;
  height: 6.678dvh;
  transition:
    transform 0.25s ease,
    filter 0.25s ease;
}

.imgLuque:hover {
  transform: scale(1.05);
  filter: brightness(1.05);
}

.navScrollable {
  height: 100%;
  overflow-y: auto;
  scrollbar-width: none;
}

.NavMuni {
  justify-content: center;
  flex-shrink: 0;
}

.bn3 {
  text-align: center;
  height: 8.347dvh;
  padding: 1.669dvh 0.366vw;
  border-radius: 0.25rem;
  border-bottom: 1px solid var(--text-primary);
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease;
}
.bn3:hover:not(:has(.router-link-exact-active)) {
  background: rgba(0, 195, 168, 0.12);
  box-shadow: inset 0 0 0 var(--color-success);
}

/* Estilos de los Badges */
.image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.757vw;
  height: 4.007dvh;
  position: relative;
  margin-left: auto; /* Empuja el badge a la derecha */
}

.image-container img {
  width: 1.537vw;
  aspect-ratio: 1 / 1;
  /* height: 3.506dvh; */
}

.iconos {
  width: 2.196vw;
  height: 5.008dvh;
}

.turnos-value {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  line-height: 1;
  font-size: 14px;
  font-weight: bold;
  color: var(--blanco);
}

.gap {
  gap: 0.73vw;
}

.bn3:has(.router-link-exact-active) {
  background: rgba(0, 195, 168, 0.2);
}

@media (min-width: 1400px) {
  .iconos {
    width: 35px;
    height: 35px;
  }
}

.panelGeneral.open {
  transform: translateX(0);
}

@media (min-width: 769px) {
  .panelGeneral {
    transform: translateX(0);
  }
}

/* ------------------ MOBILE ------------------ */
@media (max-width: 768px) {
  .panelGeneral {
    width: fit-content;
    padding: 2.4dvh 4.44vw;
  }
  .imgLuque {
    width: 36.3vw;
  }
  .logo-container {
    gap: 5.56vw;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    align-items: flex-end;
  }
  .arrow-close {
    width: 6.93vw;
    height: 3.18dvh;
    margin-bottom: 0.4rem;
  }
  .iconos {
    width: 8.31vw;
  }
  .bn3 {
    padding: 1.28dvh 1.38vw;
  }
  .actions-container {
    display: flex;
    gap: 5.56vw;
    padding: 0 10.53vw 2.67dvh 10.53vw;
  }
  .action {
    width: 11.11vw;
  }
  .image-container {
    width: 7.22vw;
  }
  .image-container img {
    width: 6vw;
  }
}
</style>
